import { useCallback, useEffect, useState, type CSSProperties, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import useEmblaCarousel from "embla-carousel-react";
import { pageDots } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { ImageWithFallback } from "../internal-ui/image-with-fallback";
import { PW } from "../internal-ui/pewter-shell";

/** A round button floating on the photo (heart, share, ...). */
export interface PhoneGalleryAction {
  key: string;
  label: string;
  icon: string;
  onPress: () => void;
  /** Set for a toggle (the heart), so a screen reader says on or off. */
  pressed?: boolean;
  iconColor?: string;
  /** Turn the icon round in Arabic, e.g. the share arrow. */
  flipInRtl?: boolean;
  testId?: string;
}

export interface PhoneGalleryProps {
  images: string[];
  title: string;
  /** Opens the full-screen photos at this one. */
  onOpenPhoto: (index: number) => void;
  /** The venue's reel: a navy "Video" chip beside the counter. */
  onVideo?: () => void;
  /** Stands in for the photos when there are none, e.g. the reel's poster. */
  fallbackImage?: string | null;
  /** The back button at the start corner. Left out when not given. */
  onBack?: () => void;
  /** The round buttons at the end corner, in order. */
  actions?: PhoneGalleryAction[];
  /** The breakpoint class that hides this from the desktop layout. */
  className?: string;
  testId?: string;
}

/** Same radius as the desktop mosaic frame, close to the app's card. */
export const PHONE_GALLERY_RADIUS = 26;
/* Fixed, so nothing below moves while the photo loads. */
const PHOTO_HEIGHT = "clamp(320px, 95vw, 460px)";
const EMPTY_HEIGHT = "clamp(200px, 55vw, 300px)";

/* Frosted white, like the app's glass buttons, readable on any photo. */
const ROUND_STYLE: CSSProperties = {
  background: "rgba(255,255,255,0.86)",
  color: PW.navy,
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  boxShadow: "0 0 0 1px rgba(17,14,57,0.08), 0 2px 8px rgba(4,0,51,0.16)",
};
const COUNTER_STYLE: CSSProperties = {
  background: "rgba(255,255,255,0.88)",
  color: PW.navy,
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
};
const VIDEO_STYLE: CSSProperties = {
  background: "rgba(17,14,57,0.86)",
  color: "#fff",
};

const DOT_CLASS: Record<"active" | "normal" | "small", string> = {
  active: "h-1.5 w-4 bg-white",
  normal: "h-1.5 w-1.5 bg-white/65",
  small: "h-1 w-1 bg-white/55",
};

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

/**
 * The photos at the top of a page on phones and tablets, drawn like the app's
 * gallery: one rounded card, swiped photo by photo, with round buttons on top
 * (back at the start, the page's actions at the end) and one row along the
 * bottom: the "1/6" counter and the "Video" chip at the start, at most five
 * page dots at the end. One row, so the dots can never sit on the chips
 * (owner, 2026-10-15: twenty dots ran over "Video" in the app).
 */
export function PhoneGallery({
  images,
  title,
  onOpenPhoto,
  onVideo,
  fallbackImage,
  onBack,
  actions = [],
  className,
  testId = "photo-swipe",
}: PhoneGalleryProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const reducedMotion = usePrefersReducedMotion();
  const list = images.filter((src) => typeof src === "string" && src.trim().length > 0);
  const count = list.length;
  const [active, setActive] = useState(0);
  const [moved, setMoved] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    direction: isRtl ? "rtl" : "ltr",
    // Reduced motion: a near-instant settle instead of the glide.
    duration: reducedMotion ? 1 : 25,
    watchDrag: count > 1,
  });
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const snap = emblaApi.selectedScrollSnap();
    setActive(snap);
    if (snap !== 0) setMoved(true);
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Arrow keys page the photos; in Arabic the photos run right to left, so
  // the left arrow goes forward.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!emblaApi || count < 2) return;
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const forward = (e.key === "ArrowRight") !== isRtl;
    if (forward) emblaApi.scrollNext(reducedMotion);
    else emblaApi.scrollPrev(reducedMotion);
  };

  const dots = pageDots(count, active);
  const fallback = fallbackImage?.trim() || null;

  return (
    <div
      className={`relative overflow-hidden ${className ?? ""}`}
      style={{ borderRadius: PHONE_GALLERY_RADIUS, background: "#eceaf4" }}
      data-testid={testId}
      data-count={count}
    >
      {count > 0 ? (
        <div
          className="overflow-hidden"
          ref={emblaRef}
          role="region"
          aria-roledescription="carousel"
          aria-label={title}
          onKeyDown={onKeyDown}
        >
          <div className="flex">
            {list.map((src, i) => (
              <button
                key={`${src}-${i}`}
                type="button"
                onClick={() => onOpenPhoto(i)}
                aria-label={t("activity.open_photo", {
                  index: i + 1,
                  count,
                  defaultValue: "Open photo {{index}} of {{count}}",
                })}
                // Only the photo on show is in the tab order, so tabbing never
                // lands on one that is off the card.
                tabIndex={i === active ? 0 : -1}
                className="relative block min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
                style={{ flex: "0 0 100%", height: PHOTO_HEIGHT }}
                data-testid="phone-photo"
              >
                <ImageWithFallback
                  src={src}
                  alt={`${title} ${i + 1}`}
                  className="block h-full w-full object-cover"
                  // The first photo stays lazy: it is preloaded for this screen
                  // only (see ActivityGallery). The ones either side of the
                  // photo on show load once the guest swipes, so the next swipe
                  // never lands on grey (and a desktop, which hides this, never
                  // fetches them).
                  loading={moved && i !== 0 && Math.abs(i - active) <= 1 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "auto"}
                  decoding={i === 0 ? "sync" : "async"}
                  sizes="100vw"
                  srcsetWidths={[480, 720, 1080, 1440, 1920]}
                />
              </button>
            ))}
          </div>
        </div>
      ) : fallback ? (
        <div style={{ height: PHOTO_HEIGHT }} data-testid="phone-photo-fallback">
          <ImageWithFallback
            src={fallback}
            alt={title}
            className="block h-full w-full object-cover"
            loading="lazy"
            sizes="100vw"
            srcsetWidths={[480, 720, 1080, 1440]}
          />
        </div>
      ) : (
        <div
          className="flex w-full items-center justify-center"
          style={{ height: EMPTY_HEIGHT, color: "rgba(17,14,57,0.28)" }}
          data-testid="phone-photo-empty"
          aria-hidden="true"
        >
          <Icon name="ri-image-line" className="text-[40px]" aria-hidden />
        </div>
      )}

      {/* Shade at the top and bottom so the buttons read on a white photo. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 68%, rgba(0,0,0,0.30) 100%)",
        }}
      />

      {(onBack || actions.length > 0) && (
        <div className="pointer-events-none absolute inset-x-3 top-3 z-[2] flex items-start justify-between gap-2">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label={t("common.back", { defaultValue: "Back" })}
              className="pointer-events-auto inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full motion-safe:transition-transform motion-safe:active:scale-[0.94]"
              style={ROUND_STYLE}
              data-testid="top-back"
            >
              <Icon name="ri-arrow-left-s-line" className="text-[22px] rtl:-scale-x-100" aria-hidden />
            </button>
          ) : (
            <span />
          )}
          {actions.length > 0 && (
            <div className="pointer-events-auto flex shrink-0 items-center gap-2">
              {actions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  onClick={action.onPress}
                  aria-label={action.label}
                  aria-pressed={action.pressed}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full motion-safe:transition-transform motion-safe:active:scale-[0.94]"
                  style={ROUND_STYLE}
                  data-testid={action.testId}
                >
                  <Icon
                    name={action.icon}
                    className={`text-[17px] ${action.flipInRtl ? "rtl:-scale-x-100" : ""}`}
                    color={action.iconColor}
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {(count > 1 || onVideo) && (
        /* One row: chips at the start, dots at the end. The dots never
           shrink and the chips never grow into them. */
        <div
          className="pointer-events-none absolute inset-x-3 bottom-3 z-[2] flex items-center justify-between gap-3"
          data-testid="photo-bottom-row"
        >
          <div className="flex min-w-0 items-center gap-1.5">
            {count > 1 && (
              <button
                type="button"
                onClick={() => onOpenPhoto(active)}
                aria-label={t("activity.view_all_photos", {
                  count,
                  defaultValue: "View all {{count}} photos",
                })}
                className="pointer-events-auto inline-flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 font-sans text-[12px] font-bold"
                style={COUNTER_STYLE}
                data-testid="photo-counter"
              >
                <Icon name="ri-image-line" className="text-[13px]" aria-hidden />
                {/* Latin digits, "2/20" in every locale, ar included. */}
                <span dir="ltr">
                  {active + 1}/{count}
                </span>
              </button>
            )}
            {onVideo && (
              <button
                type="button"
                onClick={onVideo}
                className="pointer-events-auto inline-flex h-7 min-w-0 items-center gap-1 rounded-full px-2.5 font-sans text-[12px] font-bold"
                style={VIDEO_STYLE}
                data-testid="watch-video"
              >
                <Icon name="ri-play-fill" className="shrink-0 text-[13px]" aria-hidden />
                <span>{t("activity.video", { defaultValue: "Video" })}</span>
              </button>
            )}
          </div>
          {dots.length > 0 && (
            <div className="flex shrink-0 items-center gap-1" aria-hidden="true" data-testid="page-dots">
              {dots.map((dot) => (
                <span
                  key={dot.index}
                  className={`block rounded-full motion-safe:transition-all motion-safe:duration-200 ${DOT_CLASS[dot.size]}`}
                  data-testid="page-dot"
                  data-size={dot.size}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
